import type { AppSettings } from "@lucky-try/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Field, Input } from "../components/ui/form";
import { Badge, Card, ErrorState, Loading, PageHeader } from "../components/ui/misc";
import { api, errorMessage } from "../lib/api";
import { integrationsQuery, settingsQuery } from "../lib/queries";
import { useCan } from "../lib/session";

export function SettingsPage() {
  const queryClient = useQueryClient();
  const canWrite = useCan("settings.write");
  const settings = useQuery(settingsQuery);
  const integrations = useQuery(integrationsQuery);
  const [draft, setDraft] = useState<AppSettings | null>(null);

  const save = useMutation({
    mutationFn: (body: AppSettings) =>
      api<AppSettings>("/admin/settings", { method: "PATCH", body }),
    onSuccess: async () => {
      setDraft(null);
      await queryClient.invalidateQueries({ queryKey: ["settings"] });
      toast.success("Settings saved");
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  if (settings.error)
    return <ErrorState error={settings.error} onRetry={() => void settings.refetch()} />;
  if (settings.isPending) return <Loading />;

  const values = draft ?? settings.data;
  const patch = (next: Partial<AppSettings>) => setDraft({ ...values, ...next });

  const rows: { label: string; ok: boolean; detail?: string }[] = integrations.data
    ? [
        { label: "Environment", ok: true, detail: integrations.data.environment },
        {
          label: "Email",
          ok: integrations.data.email.configured,
          detail: integrations.data.email.provider,
        },
        { label: "Turnstile", ok: integrations.data.turnstile.configured },
        { label: "Catalog deploy hook", ok: integrations.data.catalogDeployHook.configured },
        { label: "Catalog build token", ok: integrations.data.catalogBuildToken.configured },
        { label: "GamePush", ok: integrations.data.gamepush.configured },
        { label: "RoiAds tracking", ok: integrations.data.roiads.enabled },
        {
          label: "Media storage",
          ok: integrations.data.mediaStorage.configured,
          detail: integrations.data.mediaStorage.publicBaseUrl ?? undefined,
        },
      ]
    : [];

  return (
    <>
      <PageHeader title="Settings" description="Operational preferences and integration health." />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card
          title="Preferences"
          actions={
            canWrite && (
              <Button
                variant="primary"
                size="sm"
                disabled={draft === null || save.isPending}
                onClick={() => save.mutate(values)}
              >
                Save
              </Button>
            )
          }
        >
          <div className="flex flex-col gap-3">
            <Field
              label="Feedback notification address"
              hint="Bug reports and contact messages are forwarded here."
            >
              <Input
                type="email"
                disabled={!canWrite}
                value={values["feedback.notifyEmail"] ?? ""}
                onChange={(event) => patch({ "feedback.notifyEmail": event.target.value || null })}
              />
            </Field>
            <Field
              label="Analytics retention (days)"
              hint="Play sessions older than this are purged nightly."
            >
              <Input
                type="number"
                min={7}
                max={730}
                disabled={!canWrite}
                value={values["analytics.retentionDays"]}
                onChange={(event) =>
                  patch({ "analytics.retentionDays": Number(event.target.value) })
                }
              />
            </Field>
          </div>
        </Card>

        <Card title="Integrations">
          <p className="text-muted text-caption mb-3">
            Credentials live in Worker secrets and are never displayed here — only whether each one
            is configured.
          </p>
          {integrations.isPending ? (
            <Loading />
          ) : (
            <table className="text-ui w-full border-collapse text-left">
              <tbody>
                {rows.map((row) => (
                  <tr key={row.label} className="t-row border-line/60 border-b last:border-0">
                    <td className="text-body h-9 pr-3">{row.label}</td>
                    <td className="text-muted text-caption px-3">{row.detail ?? ""}</td>
                    <td className="w-24 text-right">
                      <Badge tone={row.ok ? "success" : "warning"}>
                        {row.ok ? "configured" : "missing"}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  );
}
